import { extend } from 'flarum/common/extend';
import ModalManager from 'flarum/common/components/ModalManager';

/**
 * Two things core does to every modal that only matter on a desktop, and that
 * on an iPhone are the difference between a form that takes typing and one
 * that does not.
 *
 * ── body-scroll-lock ──────────────────────────────────────────────────────
 * On open, core calls disableBodyScroll() on the modal manager. On iOS that
 * library installs a document-wide touchmove preventDefault and takes over
 * the body's positioning so the page behind cannot scroll. A phone modal is
 * already the full viewport (`.Modal-content { min-height: 100dvh }`), so
 * there is nothing behind it to stop scrolling — and the takeover is exactly
 * the kind of body-level change that makes Safari drop a focused field.
 *
 * `activeDialogManagerElement` is read in one place, as the lock's target and
 * its guard; answering null on a phone means the lock is simply never taken.
 *
 * ── focus-trap ────────────────────────────────────────────────────────────
 * The trap exists to keep Tab from leaving the dialog. There is no Tab key on
 * a phone, and the trap's first act — focusing the first field — is what pops
 * the keyboard and scrolls the form (see settleModalTop). Off, on phones.
 * returnFocus: false is essential: deactivate() otherwise hands focus back to
 * whatever was focused before the modal opened, i.e. straight out of it.
 *
 * Both run only under the phone media query; a desktop keeps core's behavior
 * untouched.
 */
function isPhone() {
  return window.matchMedia('(max-width: 767px)').matches;
}
export default function modalPhoneFixes() {
  const proto = ModalManager.prototype;
  const target = Object.getOwnPropertyDescriptor(proto, 'activeDialogManagerElement');
  if (target?.get) {
    Object.defineProperty(proto, 'activeDialogManagerElement', {
      configurable: true,
      get() {
        return isPhone() ? null : target.get.call(this);
      }
    });
  }

  // After core's own onupdate, which is where it creates and activates the trap.
  extend(proto, 'onupdate', function () {
    if (isPhone() && this.focusTrap) {
      this.focusTrap.deactivate?.({
        returnFocus: false
      });
    }
  });
}